import { useId, useState, type ReactNode } from "react";
import { cn } from "@/lib/cn";
import styles from "./ui.module.css";

export interface TabItem {
  id: string;
  label: ReactNode;
  content: ReactNode;
  disabled?: boolean;
}

export interface TabsProps {
  items: TabItem[];
  value?: string;
  defaultValue?: string;
  onValueChange?: (id: string) => void;
  className?: string;
}

export function Tabs({
  items,
  value,
  defaultValue,
  onValueChange,
  className,
}: TabsProps) {
  const autoId = useId();
  const initial = defaultValue ?? items[0]?.id ?? "";
  const [internal, setInternal] = useState(initial);
  const active = value ?? internal;
  const activeItem = items.find((item) => item.id === active) ?? items[0];

  function select(id: string) {
    if (value === undefined) {
      setInternal(id);
    }
    onValueChange?.(id);
  }

  return (
    <div className={className}>
      <div className={styles.tabsList} role="tablist">
        {items.map((item) => {
          const selected = item.id === active;
          return (
            <button
              key={item.id}
              type="button"
              role="tab"
              id={`${autoId}-${item.id}`}
              aria-selected={selected}
              aria-controls={`${autoId}-panel-${item.id}`}
              disabled={item.disabled}
              className={cn(styles.tab, selected && styles.tabActive)}
              onClick={() => select(item.id)}
            >
              {item.label}
            </button>
          );
        })}
      </div>
      {activeItem ? (
        <div
          role="tabpanel"
          id={`${autoId}-panel-${activeItem.id}`}
          aria-labelledby={`${autoId}-${activeItem.id}`}
          className={styles.tabPanel}
        >
          {activeItem.content}
        </div>
      ) : null}
    </div>
  );
}
